import {
  Card,
  Col,
  Create,
  Divider,
  Form,
  Input,
  Row,
  Select,
  notification,
  useForm,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { SingleDatePicker } from 'components/date-picker/SingleDatePicker';
import { ProductSaleTable } from 'components/product-sale-table';
import dayjs from 'dayjs';
import { EStatus } from 'enums';
import { IClient, ISale } from 'interfaces';
import { useEffect, useReducer, useRef, useState } from 'react';
import { ActionKind, initialState, productSaleReducer } from 'reducers/product-sale-reducer';
import { getClient, getClientsDropdown } from 'services/client';
import { getPOCOfClient } from 'services/point-of-contact';
import { createSalesStockCorrection } from 'services/sale';
import { getTransactionToken } from 'services/transaction';
import { IWarehouseDropDown, getWarehousesDropdown } from 'services/warehouse';
import { getAddressAsString } from 'utilities/shared';

interface IClientPOC {
  id: string;
  name: string;
}

const { Option } = Select;

export const SalesStockCorrectionCreate: React.FC = () => {
  const { list } = useNavigation();
  const { form, formProps, saveButtonProps, queryResult } = useForm<ISale>({
    metaData: {
      fields: ['id'],
    },
  });

  const [warehouses, setWarehouses] = useState<IWarehouseDropDown[]>([]);
  const [clients, setClients] = useState<
    Omit<IClient, 'address' | 'pointOfContacts' | 'clientPhoto' | 'clientTags' | 'payMethods'>[]
  >([]);
  const [clientPOCs, setClientPOCs] = useState<IClientPOC[]>([]);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [salesDate, setSalesDate] = useState<Date>();
  const [
    { saleProducts, warehouseId, vat, shipment, discountCost, paidInCash, payNow, fullDue },
    dispatch,
  ] = useReducer(productSaleReducer, initialState);
  const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState<boolean>(false);

  const transactionTokenRef = useRef<string>();
  const transactionTokenRequestRef = useRef<boolean>(false);
  const warehouseSelectRef = useRef<any>(null);
  const clientSelectRef = useRef<any>(null);
  const datePickerRef = useRef<any>(null);
  const pocSelectRef = useRef<any>(null);
  const salesAddressRef = useRef<any>(null);

  useEffect(() => {
    (async () => {
      setIsFetching(true);
      try {
        const [warehousesResponse, clientsResponse] = await Promise.all([
          getWarehousesDropdown(),
          getClientsDropdown(),
        ]);
        const warehousesData = warehousesResponse.map((warehouse) => {
          return {
            id: warehouse.id,
            name: warehouse.name,
          };
        });

        setClients(clientsResponse);
        setWarehouses(warehousesData);

        // after fetching the data, focus on the warehouse dropdown
        setTimeout(() => {
          warehouseSelectRef.current?.focus();
          setWarehouseDropdownOpen(true);
        }, 0);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
        setClients([]);
        setWarehouses([]);
      }
      setIsFetching(false);
    })();
  }, []);

  const getOptions = (items: IClient[] | IClientPOC[] | IWarehouseDropDown[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const onSelectWarehouse = async (warehouseId: string | undefined) => {
    if (!warehouseId) return;
    dispatch({ type: ActionKind.SetWarehouseId, payload: warehouseId });

    // after selecting the warehouse, focus on the client dropdown
    setTimeout(() => {
      clientSelectRef.current?.focus();
    }, 0);
  };

  const onSelectClient = async (clientId: string) => {
    // after selecting the client, focus on the date picker
    setTimeout(() => {
      datePickerRef.current?.focus();
    }, 0);

    try {
      const [client, pocs] = await Promise.all([getClient(clientId), getPOCOfClient(clientId)]);
      const address = client.address;
      const addressString = getAddressAsString(address || {});

      setClientPOCs([...pocs]);
      form.setFieldsValue({
        salesAddress: addressString,
        clientPoc: pocs[0]?.id,
      });
    } catch (err: any) {
      setClientPOCs([]);
      form.setFieldsValue({
        salesAddress: undefined,
        clientPoc: undefined,
      });
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
  };

  const onFinish = async (values: any) => {
    if (transactionTokenRequestRef.current === true) return;

    try {
      setIsCreating(true);

      if (!transactionTokenRef.current && transactionTokenRequestRef.current === false) {
        transactionTokenRequestRef.current = true;
        transactionTokenRef.current = await getTransactionToken();
      }

      const saleData = {
        client: values.client as string,
        clientPoc: values.clientPoc as string,
        salesDate: values.salesDate ? new Date(values.salesDate) : new Date(),
        salesStatus: EStatus.CONFIRMED,
        warehouse: values.warehouse as string,
        salesAddress: values.salesAddress as string,
        note: values.note as string,
        vatAmount: vat,
        shipmentCost: shipment,
        discountCost: discountCost,
        stockCorrectionSalesProducts: saleProducts.map((product) => ({
          sku: product.sku,
          quantity: product.quantity,
          unitPrice: product.unitPrice,
          productVariantId: product.productVariantId,
        })),
        transactionToken: transactionTokenRef.current!,
      };

      await createSalesStockCorrection(saleData);
      notification.success({
        message: 'Success',
        description: 'Successfully created a sale stock correction.',
      });
      list('stock-correction-sales');
    } catch (err: any) {
      transactionTokenRef.current = undefined;
      transactionTokenRequestRef.current = false;
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Create
        isLoading={queryResult?.isFetching}
        saveButtonProps={{
          ...saveButtonProps,
          disabled: !saleProducts.length,
          loading: isCreating,
        }}
      >
        <Form
          {...formProps}
          layout="vertical"
          initialValues={{
            isActive: true,
          }}
          onFinish={onFinish}
        >
          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <Card title="Sale Information">
                <Form.Item
                  label="Warehouse"
                  name="warehouse"
                  rules={[{ required: true, message: 'Please select a warehouse' }]}
                >
                  <Select
                    ref={warehouseSelectRef}
                    autoFocus
                    open={warehouseDropdownOpen}
                    showSearch
                    placeholder="Select a warehouse"
                    loading={isFetching}
                    disabled={isFetching || !warehouses.length}
                    onSelect={(value: string) => {
                      setWarehouseDropdownOpen(false);
                      onSelectWarehouse(value?.toString());
                    }}
                    onDropdownVisibleChange={(visible) => setWarehouseDropdownOpen(visible)}
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {getOptions(warehouses)}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Client's Name"
                  name="client"
                  rules={[{ required: true, message: 'Please select a client' }]}
                >
                  <Select
                    ref={clientSelectRef}
                    showSearch
                    placeholder="Select a client"
                    loading={isFetching}
                    disabled={isFetching || !clients.length}
                    onSelect={onSelectClient}
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {getOptions(clients)}
                  </Select>
                </Form.Item>
                <Form.Item label="Date" name="salesDate">
                  <SingleDatePicker
                    ref={datePickerRef}
                    value={dayjs(salesDate)}
                    onChange={(value: any, _formatString: string) => {
                      setSalesDate(new Date(value.toDate()));
                      pocSelectRef.current?.focus();
                    }}
                  />
                </Form.Item>
                <Form.Item label="Point Of Contact" name="clientPoc">
                  <Select
                    ref={pocSelectRef}
                    showSearch
                    placeholder="Select a POC"
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    onDropdownVisibleChange={(visible) => {
                      if (!visible) {
                        salesAddressRef.current?.focus();
                      }
                    }}
                  >
                    {getOptions(clientPOCs)}
                  </Select>
                </Form.Item>
                <Form.Item label="Sale Address" name="salesAddress">
                  <Input.TextArea ref={salesAddressRef} rows={4} />
                </Form.Item>
                <Form.Item label="Note" name="note">
                  <Input.TextArea rows={4} />
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <Divider />

          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <ProductSaleTable
                saleProducts={saleProducts}
                warehouse={warehouseId}
                vat={vat}
                shipment={shipment}
                discountCost={discountCost}
                paidInCash={paidInCash}
                payNow={payNow}
                fullDue={fullDue}
                productDispatcher={dispatch}
              />
            </Col>
          </Row>
        </Form>
      </Create>
    </>
  );
};
